import React, { useCallback, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useLocation } from "react-router";
import { FaComments, FaThumbtack, FaTrash } from "react-icons/fa";
import { Conversation } from "@/models/conversation";
import { createChatDragPayload } from "@/utils/drag-types";
import { usePanelNavigation } from "@/hooks/use-panel-route";
import SidebarItem, { SidebarItemAction } from "./sidebar-item";
import ResourceContextMenu from "./resource-context-menu";

// Data transfer type for conversation drag & drop
export const CONVERSATION_DRAG_TYPE = "application/x-conversation";

// Drop mode: 'reorder' for moving between items
export type ConversationDropMode = "reorder";

interface ConversationItemProps {
  conversation: Conversation;
  isPinned?: boolean;
  onUnpin?: () => void;
  onDelete?: () => void;
  // Inline rename
  isRenaming?: boolean;
  onRenameSubmit?: (newName: string) => void;
  onRenameCancel?: () => void;
  // Multi-select props
  /** Whether this item is selected */
  isSelected?: boolean;
  /** Ordered list of all conversation IDs for range selection */
  orderedIds?: string[];
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
}

const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isPinned = false,
  onUnpin,
  onDelete,
  isRenaming = false,
  onRenameSubmit,
  onRenameCancel,
  isSelected,
  orderedIds = [],
  onMultiSelectContextMenu,
}) => {
  // Panel navigation hook for split-view compatible navigation
  const { navigateTo } = usePanelNavigation();
  const location = useLocation();

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  // Check if this conversation is active
  const searchParams = new URLSearchParams(location.search);
  const activeConversationId = searchParams.get("id");
  const isActive = activeConversationId === conversation.id;

  const displayName = conversation.name || "Sans titre";

  // Create drag payload
  const dragPayload = createChatDragPayload(conversation.id!, displayName);

  const handleClick = useCallback(() => {
    navigateTo(`/chat?id=${conversation.id}`, { title: displayName });
  }, [conversation.id, displayName, navigateTo]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleRenameSubmit = useCallback(
    async (newName: string) => {
      onRenameSubmit?.(newName);
    },
    [onRenameSubmit]
  );

  const handleUnpin = useCallback(() => {
    onUnpin?.();
  }, [onUnpin]);

  const handleDelete = useCallback(() => {
    onDelete?.();
  }, [onDelete]);

  // Build hover actions
  const hoverActions = (
    <>
      {isPinned && (
        <SidebarItemAction
          onClick={handleUnpin}
          title="Désépingler"
          icon={<FaThumbtack size={10} />}
        />
      )}
      {onDelete && (
        <SidebarItemAction
          onClick={handleDelete}
          title="Supprimer"
          icon={<FaTrash size={10} />}
          variant="danger"
        />
      )}
    </>
  );

  // Pinned indicator (shown when not hovered)
  const statusIndicator = isPinned ? (
    <FaThumbtack size={10} className="arc-sidebar-pin-icon" />
  ) : null;

  return (
    <>
      <SidebarItem
        id={conversation.id!}
        name={displayName}
        resourceType="conversation"
        icon={
          <div className="w-5 h-5 rounded flex items-center justify-center bg-[var(--arc-sidebar-accent-glow)]">
            <FaComments size={10} className="text-[var(--arc-accent)]" />
          </div>
        }
        isActive={isActive}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        isSelected={isSelected}
        orderedIds={orderedIds}
        onMultiSelectContextMenu={onMultiSelectContextMenu}
        enableDrag={true}
        dragPayload={dragPayload}
        isRenaming={isRenaming}
        onRenameSubmit={handleRenameSubmit}
        onRenameCancel={onRenameCancel}
        hoverActions={hoverActions}
        statusIndicator={statusIndicator}
      />

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && (
          <ResourceContextMenu
            position={contextMenu}
            dragPayload={dragPayload}
            onClose={closeContextMenu}
          />
        )}
      </AnimatePresence>
    </>
  );
};

ConversationItem.displayName = "ConversationItem";

export default ConversationItem;
